/**
 * Film Search Component
 * Shows 24 results per page with improved pagination layout
 */

import { useState, useEffect } from '@wordpress/element';
import { useFilms } from '../context/FilmContext';
import FilmCard from './FilmCard';

export default function FilmSearch() {
    const { searchFilms, fetchGenres, films, genres, loading } = useFilms();
    const [query, setQuery] = useState('');
    const [selectedGenre, setSelectedGenre] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasSearched, setHasSearched] = useState(false);

    const t = window.rialtoData?.translations || {};

    // Generate year options (current year back to 1920)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => currentYear - i);

    useEffect(() => {
        fetchGenres();

        // Read URL params on load
        const params = new URLSearchParams(window.location.search);
        const urlQuery = params.get('q');
        const urlGenre = params.get('genre');
        const urlYear = params.get('year');

        if (urlGenre) setSelectedGenre(urlGenre);
        if (urlYear) setSelectedYear(urlYear);

        if (urlQuery || urlGenre || urlYear) {
            if (urlQuery) setQuery(urlQuery);
            // Trigger search on next tick after state is set
            setTimeout(() => {
                const searchParams = { page: 1, per_page: 24 };
                if (urlQuery) searchParams.q = urlQuery;
                if (urlGenre) searchParams.genre = urlGenre;
                if (urlYear) searchParams.year = urlYear;
                searchFilms(searchParams).then((result) => {
                    setTotalPages(result.total_pages || 1);
                    setHasSearched(true);
                });
            }, 0);
        }
    }, []);

    useEffect(() => {
        if (hasSearched) {
            performSearch();
        }
    }, [page]);

    const performSearchWithQuery = async (q) => {
        const params = { page: 1, per_page: 24 };
        if (q) params.q = q;
        const result = await searchFilms(params);
        setTotalPages(result.total_pages || 1);
        setHasSearched(true);
    };

    const performSearch = async () => {
        const params = { page, per_page: 24 };

        if (query) params.q = query;
        if (selectedGenre) params.genre = selectedGenre;
        if (selectedYear) params.year = selectedYear;

        const result = await searchFilms(params);
        setTotalPages(result.total_pages || 1);
        setHasSearched(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPage(1);
        performSearch();
    };

    const handleReset = () => {
        setQuery('');
        setSelectedGenre('');
        setSelectedYear('');
        setPage(1);
        setHasSearched(false);
    };

    return (
        <div className="rialto-search">
            <form className="rialto-search-form" onSubmit={handleSubmit}>
                <input
                    type="text"
                    className="rialto-search-input"
                    placeholder={t.searchPlaceholder || 'Search films, directors, actors...'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />

                <select
                    className="rialto-search-select"
                    value={selectedGenre}
                    onChange={(e) => setSelectedGenre(e.target.value)}
                >
                    <option value="">{t.allGenres || 'All genres'}</option>
                    {genres.map((genre) => (
                        <option key={genre.slug} value={genre.slug}>
                            {genre.name}
                        </option>
                    ))}
                </select>

                <select
                    className="rialto-search-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                >
                    <option value="">{t.allYears || 'All years'}</option>
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>

                <button type="submit" className="rialto-btn rialto-btn-primary">
                    {t.search || 'Search'}
                </button>
            </form>

            {loading && (
                <div className="rialto-loading">
                    <div className="rialto-spinner"></div>
                </div>
            )}

            {hasSearched && !loading && (
                <>
                    {films.length > 0 ? (
                        <>
                            <div className="rialto-film-grid">
                                {films.map((film) => (
                                    <FilmCard key={film.id} film={film} />
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className="rialto-pagination">
                                    {/* Previous button - hidden on first page */}
                                    <div className="rialto-pagination-prev">
                                        {page > 1 && (
                                            <button
                                                className="rialto-btn rialto-btn-secondary"
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                            >
                                                <span className="rialto-pagination-label">&larr; Previous</span>
                                                <span className="rialto-pagination-arrow">&larr;</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Page info - always centred */}
                                    <span className="rialto-pagination-info">
                                        Page {page} of {totalPages}
                                    </span>

                                    {/* Next button - hidden on last page */}
                                    <div className="rialto-pagination-next">
                                        {page < totalPages && (
                                            <button
                                                className="rialto-btn rialto-btn-secondary"
                                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            >
                                                <span className="rialto-pagination-label">Next &rarr;</span>
                                                <span className="rialto-pagination-arrow">&rarr;</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="rialto-no-results">{t.noResults || 'No films found.'}</p>
                    )}
                </>
            )}
        </div>
    );
}
